import { Component, OnDestroy, OnInit, ViewChildren } from '@angular/core';
import { UntypedFormArray } from '@angular/forms';
import { Router } from '@angular/router';
import { PrFormAbstract } from '@app/components/pr-form/pr-form.abstract';
import { PrFormConstructor } from '@app/components/pr-form/pr-form.contructor';
import { EstateDao } from '@app/dao/estate/estate.dao';
import { ReferenceDao } from '@app/dao/reference/reference.dao';
import { UserDao } from '@app/dao/user/user.dao';
import { ScpiService } from '@app/services/scpi.service';
import * as _ from 'lodash';
import moment from 'moment';
import { GoogleAnalyticsService } from 'ngx-google-analytics';
import { BehaviorSubject, Subject, combineLatest, takeWhile } from 'rxjs';
import { EaFeesFormComponent } from '../ea-fees-form/ea-fees-form.component';
import { EaScpiFormService } from './ea-scpi-form.service';

@Component({
  selector: 'ea-scpi-form',
  templateUrl: './ea-scpi-form.component.html',
  styleUrls: ['./ea-scpi-form.component.scss'],
  standalone: false,
})
export class EaScpiFormComponent
  extends PrFormAbstract
  implements OnInit, OnDestroy
{
  users: any[];
  scpis: any[] = [];
  suggestedDismemberKey: number;
  _ = _;
  realEstateOwnershipTypes: any[] = [
    { id: 'PLEINE_PROPRIETE', value: 'Pleine-propriété' },
    { id: 'NU_PROPRIO', value: 'Nue-propriété' },
    { id: 'USUFRUIT', value: 'Usufruit' },
    { id: 'INDIVISION', value: 'Indivision' },
  ];
  subscriptionSource: any[] = [];
  doneSubject: Subject<void> = new Subject<void>();
  tabChanged: BehaviorSubject<any> = new BehaviorSubject(null);
  changeTab: BehaviorSubject<any> = new BehaviorSubject(null);
  alive = true;
  loading: boolean = false;
  @ViewChildren(EaFeesFormComponent) feesFormsComponents: EaFeesFormComponent;

  constructor(
    private router: Router,
    protected formConstructor: PrFormConstructor,
    private userDao: UserDao,
    private estateDao: EstateDao,
    private referenceDao: ReferenceDao,
    private scpiService: ScpiService,
    public googleAnalyticsService: GoogleAnalyticsService,
    private eaScpiFormService: EaScpiFormService,
  ) {
    super(formConstructor);
  }

  get title() {
    return this.generateTitle(
      'Modifier les parts de SCPI',
      'Ajouter des parts de SCPI',
    );
  }

  generateTitle(editTitle: string, addTitle: string) {
    if (!this.entity || !this.entity.id) return addTitle;
    if (!this.entity.label || this.entity.label === '') return editTitle;
    return `${editTitle} “${this.entity.label}”`;
  }

  ngOnInit() {
    combineLatest([
      this.userDao.getCouple(),
      this.referenceDao.getScpi(),
      this.referenceDao.getSubscriptionSource(),
    ])
      .pipe(takeWhile(() => this.alive))
      .subscribe(([users, scpis, subscriptionSource]) => {
        this.users = users;
        this.scpis = _.sortBy(scpis, 'name');
        this.subscriptionSource = subscriptionSource;
        if (!this.ownership)
          this.form
            .get('ownershipType')
            .setValue(this.realEstateOwnershipTypes[0].id);

        this.form
          .get('loans')
          .get('hasLoan')
          .valueChanges.pipe(takeWhile(() => this.alive))
          .subscribe(hasLoan =>
            hasLoan ? this.patchValueToParentValues() : undefined,
          );

        this.form
          .get('product')
          .valueChanges.pipe(takeWhile(() => this.alive))
          .subscribe(product => {
            if (!this.form.get('product').valid || !product.priceHistory.length)
              return;

            const oldestDate = this.scpiService.getOldestPriceDate(
              product.priceHistory,
            );

            if (!oldestDate) return;

            this.form
              .get('startDate')
              .setValidators([
                this.prFormService.AddValidatorsBasedOnCondition(
                  this.prFormService
                    .isTargetControlValueEqualTo(
                      'startDate',
                      val => !val || moment(val).isBefore(moment(oldestDate)),
                    )
                    .bind(this),
                  this.prFormService.CustomValidator(
                    `Doit être après le ${moment(oldestDate).format(
                      'DD/MM/YYYY',
                    )}`,
                  ),
                ),
              ]);
            this.form.get('startDate').updateValueAndValidity();
            this.setPartPrice(product, this.form.get('startDate').value);
          });

        this.form
          .get('startDate')
          .valueChanges.pipe(takeWhile(() => this.alive))
          .subscribe(startDate => {
            this.setPartPrice(this.form.get('product').value, startDate);
          });

        this.form
          .get('nbShare')
          .valueChanges.pipe(takeWhile(() => this.alive))
          .subscribe(startDate => {
            this.setPartPrice(
              this.form.get('product').value,
              this.form.get('startDate').value,
            );
            this.loading = false;
          });

        this.form
          .get('purchasePrice')
          .valueChanges.pipe(takeWhile(() => this.alive))
          .subscribe(purchasePrice => {
            let closestHistory = this.getClostestHistory();
            if (!closestHistory) {
              this.loading = false;
              return;
            }
            const nbShare = this.scpiService.calculateNbShare(
              purchasePrice,
              closestHistory.value,
            );
            this.form.get('nbShare').setValue(nbShare, { emitEvent: false });
            this.loading = false;
          });
        this.setDefaultValue();
      });
  }

  setPartPrice(product, startDate?) {
    if (!this.form.get('product').valid) return;

    if (!startDate) {
      const mostRecentDate = this.scpiService.getMostRecentPriceDate(
        product.priceHistory,
      );

      const mostRecentPrice = this.scpiService.getPartPriceAtDate(
        product.priceHistory,
        mostRecentDate,
      );

      this.form.get('startDate').setValue(mostRecentDate, { emitEvent: false });

      const nbShare = this.form.get('nbShare').value;
      const purchasePrice = this.scpiService.calculateInitialAmount(
        nbShare,
        mostRecentPrice,
      );
      this.form
        .get('purchasePrice')
        .setValue(purchasePrice, { emitEvent: false });
    } else {
      const partPrice = this.scpiService.getPartPriceAtDate(
        product.priceHistory,
        startDate,
      );

      const nbShare = this.form.get('nbShare').value;
      const purchasePrice = this.scpiService.calculateInitialAmount(
        nbShare,
        partPrice,
      );
      this.form
        .get('purchasePrice')
        .setValue(purchasePrice, { emitEvent: false });
    }
  }

  getClostestHistory() {
    if (!this.form.get('product').valid || !this.form.get('startDate').valid)
      return null;

    const startDate = this.form.get('startDate').value;
    const priceHistory = this.form.get('product').value.priceHistory;

    const partPrice = this.scpiService.getPartPriceAtDate(
      priceHistory,
      startDate,
    );

    if (partPrice === null) return null;

    return { value: partPrice };
  }

  patchValueToParentValues() {
    let estateForm = this.form.value;
    if (!estateForm) return;

    let loanForm = this.form.get('loans');
    loanForm.get('label').setValue(estateForm.label);
    loanForm.get('amountBorrowed').setValue(estateForm.purchasePrice);
    loanForm.get('startDate').setValue(estateForm.startDate);
  }

  onSubmit() {
    this.displayErrors();

    this.selectErrorInput();

    if (this.form.invalid) {
      this.changeTab.next('FIRST');
      return;
    }

    const extract = this.extract();
    let formDTOExtraction = _.merge(this.entity, extract);
    if (!extract.loans && formDTOExtraction.loans)
      formDTOExtraction.removeLoan = true;

    this.prFormService.loading();
    this.estateDao.postEstate(formDTOExtraction).subscribe(
      success => {
        let url = window.location.href;
        let hashIndex = url.indexOf('#');
        let endIndex = -1;
        let keys = ['PLEINE_PROPRIETE', 'NU_PROPRIO', 'USUFRUIT', 'INDIVISION'];

        for (let key of keys) {
          let index = url.indexOf(key);
          if (index !== -1) {
            endIndex = index;
            break;
          }
        }
        if (hashIndex !== -1 && endIndex !== -1) {
          const path = url.substring(hashIndex + 1, endIndex);
          if (formDTOExtraction.id) {
            (formDTOExtraction.removeLoan && formDTOExtraction.loans) ||
            !formDTOExtraction.loans
              ? this.router.navigate([
                  `${path}${formDTOExtraction.ownershipType}/CASH`,
                ])
              : this.router.navigate([
                  `${path}${formDTOExtraction.ownershipType}/CREDIT`,
                ]);
          }
        }
        this.prFormService.loaded();
        this.doneSubject.next();
        this.notificationService.success('La sauvegarde a réussi');
      },
      errors => {
        console.error(errors);
        this.prFormService.loaded();
        this.notificationService.error('La sauvegarde a échoué');
      },
    );
  }

  onTabChange(isLastTab: boolean) {
    this.tabChanged.next(isLastTab);
  }

  nextTab() {
    this.changeTab.next('NEXT');
  }

  patch(diff) {
    this.form.patchValue(diff);
    return false;
  }

  get scpi() {
    return this.getIfValid('product');
  }

  get date() {
    return this.getIfValid('startDate');
  }

  get dismemberDuration() {
    return this.getIfValid('dismemberDuration');
  }

  get dismemberKey() {
    return this.form.get('dismemberKey').value;
  }

  mapDismemberment(scpi) {
    return {
      NU_PROPRIO: this.mapDismembermentKeys(
        scpi.dismembermentKeys.bareOwnership,
      ),
      USUFRUIT: this.mapDismembermentKeys(scpi.dismembermentKeys.usuFruict),
    };
  }

  mapDismembermentKeys(keys) {
    var res = {};
    for (let k of keys) res[k.year] = k.value ? k.value * 100 : k.value;
    return res;
  }

  processDismembermentKey() {
    if (this.scpi && this.ownership && this.dismemberDuration) {
      if (['NU_PROPRIO', 'USUFRUIT'].includes(this.ownership)) {
        return (this.suggestedDismemberKey = this.mapDismemberment(this.scpi)[
          this.ownership
        ][this.dismemberDuration]);
      }
    }
    return (this.suggestedDismemberKey = null);
  }

  public generate = () => this.eaScpiFormService.generate(this.entity);
  public extract = () => this.eaScpiFormService.extract(this.form);

  get fees() {
    return this.form.get('fees') as UntypedFormArray;
  }

  get ownership() {
    return this.getIfValid('ownershipType');
  }

  setDefaultValue() {
    if (this.entity.owners && this.entity.owners.length)
      this.form.get('owners').setValue(this.buildInitialOwners(this.entity));
  }
  getIfValid(field) {
    if (!this.form.get(field).valid) return;
    return this.form.get(field).value;
  }

  ngOnDestroy(): void {
    this.alive = false;
  }
}
